'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import { Icon } from '@benzinga/core-ui';
import { Tooltip } from '@benzinga/core-ui';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import { NumberFormatValues, NumericFormat } from 'react-number-format';
import classNames from 'classnames';

interface InputContainerProps {
  allowNegative?: boolean;
  className?: string;
  decimalScale?: number;
  disabled?: boolean;
  isAllowed?: (value: NumberFormatValues) => boolean;
  name?: string;
  noDecimals?: boolean;
  value: number | string | null;
  onChange?: (value: number) => void;
  prefix?: string;
  suffix?: string;
  tooltip?: React.ReactNode;
  width?: number;
}

export const InputContainer: React.FC<InputContainerProps> = ({
  allowNegative = false,
  className,
  decimalScale = 2,
  disabled,
  isAllowed,
  name,
  noDecimals,
  onChange,
  prefix,
  suffix,
  tooltip,
  value,
  width,
}) => {
  const handleOnValueChange = (values: NumberFormatValues): void => {
    onChange && onChange(values.floatValue as number);
  };

  return (
    <InputWrapper className={className} name={name} tooltip={tooltip} width={width}>
      <NumericFormat
        allowNegative={allowNegative}
        decimalScale={!noDecimals && decimalScale ? decimalScale : undefined}
        decimalSeparator="."
        disabled={disabled}
        displayType="input"
        id={name}
        inputMode="numeric"
        isAllowed={isAllowed}
        name={name}
        onValueChange={handleOnValueChange}
        prefix={prefix}
        suffix={suffix}
        thousandSeparator={true}
        thousandsGroupStyle="thousand"
        value={value}
      />
    </InputWrapper>
  );
};

export interface InputWrapperProps {
  className?: string;
  children: React.ReactNode;
  name?: string;
  tooltip?: React.ReactNode;
  width?: number;
}

export const InputWrapper: React.FC<InputWrapperProps> = ({ children, className, name, tooltip, width }) => {
  return (
    <StyledInputContainer className={classNames('input-container', { [`${className}`]: !!className })} width={width}>
      {name && (
        <label htmlFor={name}>
          {name}
          {tooltip && (
            <Tooltip content={tooltip} width={250}>
              <Icon className="info-circle-icon" icon={faInfoCircle} />
            </Tooltip>
          )}
        </label>
      )}
      <div className="input-box">{children}</div>
    </StyledInputContainer>
  );
};

const StyledInputContainer = styled.div<{ width?: number }>`
  &.input-container {
    display: flex;
    flex-direction: column;

    input {
      padding: 8px;
      background-color: ${({ theme }) => theme.colors.background};
      color: #192940;
      border: 0.5px solid ${({ theme }) => theme.colorPalette.gray300};
      box-sizing: border-box;
      border-radius: 4px;
      max-width: ${({ width }) => (width ? `${width}px` : 'initial')};
      width: 100%;

      &:disabled {
        background-color: rgba(206, 221, 242, 0.06);
        color: ${({ theme }) => theme.colors.foregroundInactive};
      }
    }

    label {
      text-transform: uppercase;
      color: #5b7292;
      font-weight: ${({ theme }) => theme.fontWeight.semibold};
      margin-bottom: 4px;
      max-width: 300px;
      display: flex;
      align-items: center;
    }

    .info-circle-icon {
      font-size: ${({ theme }) => theme.fontSize.sm};
      width: 14px;
      height: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .tooltip-container {
      margin-left: 5px;
    }
  }
`;

export default InputContainer;
